/**
 * EditProfileForm Component (T171 - US11)
 * Form for editing user profile information
 * UI Design: Extracted from prototypes/figma-bquest-app/src/components/ProviderPortal.tsx settings tab
 */

import { useState, useEffect } from 'react';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Avatar } from '@/shared/components/ui/avatar';
import { Upload, Loader2 } from 'lucide-react';
import type { UserProfile } from '../api/profileApi';

type EditProfileFormProps = {
  profile: UserProfile;
  isLoading?: boolean;
  onSubmit: (data: EditProfileFormData) => void;
  onPhotoUpload: (file: File) => void;
  isUploadingPhoto?: boolean;
};

export type EditProfileFormData = {
  firstName: string;
  lastName?: string;
  email: string;
  profilePhoto?: string | null;
};

export const EditProfileForm = ({
  profile,
  isLoading = false,
  onSubmit,
  onPhotoUpload,
  isUploadingPhoto = false,
}: EditProfileFormProps) => {
  const [firstName, setFirstName] = useState(profile.firstName || '');
  const [lastName, setLastName] = useState(profile.lastName || '');
  const [email, setEmail] = useState(profile.email || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form when profile changes
  useEffect(() => {
    setFirstName(profile.firstName || '');
    setLastName(profile.lastName || '');
    setEmail(profile.email || '');
  }, [profile]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate first name
    if (!firstName || firstName.trim().length < 1) {
      newErrors.firstName = 'First name is required';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      newErrors.email = 'Invalid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName?.trim() || undefined,
      email: email.trim(),
      profilePhoto: profile.profilePhoto,
    });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ photo: 'Only image files are allowed (JPEG, PNG, GIF, WebP)' });
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setErrors({ photo: 'File size must be less than 5MB' });
      return;
    }

    // Clear photo errors
    setErrors((prev) => {
      const { photo, ...rest } = prev;
      return rest;
    });

    onPhotoUpload(file);
  };

  // Get user initials for avatar fallback
  const getInitials = (firstName: string, lastName?: string) => {
    if (lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return firstName.substring(0, 2).toUpperCase();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Profile Photo */}
      <div>
        <Label>Profile Photo</Label>
        <div className="mt-3 flex items-center gap-4">
          <Avatar className="w-20 h-20">
            {profile.profilePhoto ? (
              <img 
                src={profile.profilePhoto} 
                alt="Profile photo" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#026857] text-white flex items-center justify-center text-xl font-semibold">
                {getInitials(profile.firstName || 'U', profile.lastName)}
              </div>
            )}
          </Avatar>

          <div>
            <Label 
              htmlFor="photo-upload" 
              className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {isUploadingPhoto ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload photo
                </>
              )}
            </Label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              disabled={isUploadingPhoto}
              aria-label="Upload photo"
            />
            <p className="text-xs text-gray-500 mt-1">
              JPEG, PNG, GIF or WebP. Max 5MB.
            </p>
            {errors.photo && (
              <p className="text-xs text-red-600 mt-1">{errors.photo}</p>
            )}
          </div>
        </div>
      </div>

      {/* First Name */}
      <div>
        <Label htmlFor="firstName">First Name *</Label>
        <Input
          id="firstName"
          type="text"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            // Clear firstName error on change
            if (errors.firstName) {
              setErrors((prev) => {
                const { firstName, ...rest } = prev;
                return rest;
              });
            }
          }}
          className="mt-2"
          placeholder="Enter your first name"
          disabled={isLoading}
          aria-label="First Name"
        />
        {errors.firstName && (
          <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
        )}
      </div>

      {/* Last Name */}
      <div>
        <Label htmlFor="lastName">Last Name</Label>
        <Input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            // Clear lastName error on change
            if (errors.lastName) {
              setErrors((prev) => {
                const { lastName, ...rest } = prev;
                return rest;
              });
            }
          }}
          className="mt-2"
          placeholder="Enter your last name"
          disabled={isLoading}
          aria-label="Last Name"
        />
        {errors.lastName && (
          <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Clear email error on change
            if (errors.email) {
              setErrors((prev) => {
                const { email, ...rest } = prev;
                return rest;
              });
            }
          }}
          className="mt-2"
          placeholder="your.email@example.com"
          disabled={isLoading}
          aria-label="Email"
        />
        {errors.email && (
          <p className="text-sm text-red-600 mt-1">{errors.email}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex gap-4 pt-4">
        <Button
          type="submit"
          className="bg-[#026857] hover:bg-[#025247]"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  );
};

